import { GcfPriceLabelShapeTypeEnum, TextAlignmentEnum } from './Product';

export interface ISubproductThemeType {
  label?: IThemeConfigLabel;
  price?: IThemeConfigBase;
  nutrition?: IThemeConfigBase;
  flavor?: IThemeConfigBase;
  disclaimer?: IThemeConfigBase;
}

interface GcfPillShapePriceLabelInterface {
  backgroundColor?: string;
  border?: {
    color?: string;
    width?: string;
    style?: string;
  };
}

interface IThemeConfigLabel
  extends IThemeConfigWithHeight,
    GcfPillShapePriceLabelInterface {
  shape?: GcfPriceLabelShapeTypeEnum;
}

export interface IThemeConfigBase {
  color?: string;
  font?: string;
  fontSize?: string;
  lineHeight?: string;
  flexWrap?: string;
}

export interface IDisclaimerThemeConfigBase extends IThemeConfigBase {
  backgroundColor?: string;
  padding?: string;
  margin?: string;
}
export interface IThemeConfigWithHeight extends IThemeConfigBase {
  height?: number;
}

export interface IProductThemeType extends IThemeConfigBase {
  badgeStyle?: IBadgeStyle;
}

export interface ISubHeaderThemeType extends IThemeConfigWithHeight {
  backgroundColor?: string;
  border?: string;
  borderRadius?: string;
}

export interface IImageBadgeThemeType extends IThemeConfigBase {
  backgroundColor?: string;
  size?: string;
}

export interface IItemBadgeThemeType extends IThemeConfigBase {
  backgroundColor?: string;
  barWidth?: string;
  size?: string;
}

export interface IThemeCategories {
  [sizeName: string]: IThemeCategory;
}

export interface IThemeCategory {
  label?: IThemeConfigBase;
  price?: IThemeConfigBase;
  nutrition?: IThemeConfigBase;
}

export interface IBadgesThemeType {
  groupBadge?: IThemeConfigBase;
  itemBadge?: IItemBadgeThemeType;
  imageBadge?: IImageBadgeThemeType;
}

interface IBadgeStyle {
  font?: string;
  fontSize?: string;
  fontColor?: string;
  lineHeight?: string;
  barWidth?: string;
  badgeColor?: string;
  badgeSize?: string;
}

export interface IThemes {
  [ThemeTypeName: string]: ITheme;
}

export interface IThemeRes {
  id: number;
  name: string;
  modified_at: string;
  created_at: string;
  config: ITheme;
}

export interface ITheme {
  padding?: number;
  margin?: number;
  color: string;
  font: string;
  fontSize: string;
  lineHeight: string;
  header?: IThemeConfigWithHeight;
  headerDescription?: IThemeConfigWithHeight;
  subheader?: ISubHeaderThemeType;
  subheaderDescription?: IThemeConfigWithHeight;
  disclaimer?: IDisclaimerThemeConfigBase;
  disclaimerComponent?: IDisclaimerThemeConfigBase;
  products?: IProductThemeType;
  subproducts?: ISubproductThemeType;
  badges?: IBadgesThemeType;
  category?: IThemeCategories;
}

const ProjectConfigThemePropertiesSchema = {
  title: 'ProjectConfigThemePropertiesSchema',
  description: 'JSON Schema for ProjectConfigThemePropertiesSchema',
  type: 'object',
  properties: {
    color: { type: 'string' },
    font: { type: 'string' },
    fontSize: { type: 'string' },
    lineHeight: { type: 'string' },
    flexWrap: { type: 'string' },
  },
};

const ProjectConfigThemeProductsSchema = {
  title: 'ProjectConfigThemeProductsSchema',
  description: 'JSON Schema for ProjectConfigThemeProductsSchema',
  type: 'object',
  properties: {
    color: { type: 'string' },
    font: { type: 'string' },
    fontSize: { type: 'string' },
    lineHeight: { type: 'string' },
    badgeStyle: {
      type: 'object',
      properties: {
        font: { type: 'string' },
        fontSize: { type: 'string' },
        fontColor: { type: 'string' },
        lineHeight: { type: 'string' },
        badgeSize: { type: 'string' },
        badgeColor: { type: 'string' },
      },
    },
  },
};

const ProjectConfigThemePropertiesWithHeightSchema = {
  title: 'ProjectConfigThemePropertiesSchema',
  description: 'JSON Schema for ProjectConfigThemePropertiesSchema',
  type: 'object',
  properties: {
    color: { type: 'string' },
    font: { type: 'string' },
    fontSize: { type: 'string' },
    lineHeight: { type: 'string' },
    height: { type: 'number' },
    alignment: {
      type: 'string',
      enum: [TextAlignmentEnum.LEFT, TextAlignmentEnum.CENTER],
    },
  },
};

export const ProjectConfigThemesSchema = {
  title: 'ProjectConfigThemesSchema',
  description: 'JSON Schema for ProjectConfigThemesSchema',
  type: 'object',
  properties: {
    padding: { type: 'number' },
    margin: { type: 'number' },
    color: { type: 'string' },
    font: { type: 'string' },
    fontSize: { type: 'string' },
    lineHeight: { type: 'string' },
    header: ProjectConfigThemePropertiesWithHeightSchema,
    headerDescription: ProjectConfigThemePropertiesWithHeightSchema,
    subheader: ProjectConfigThemePropertiesWithHeightSchema,
    subheaderDescription: ProjectConfigThemePropertiesWithHeightSchema,
    disclaimer: ProjectConfigThemePropertiesSchema,
    disclaimerComponent: ProjectConfigThemePropertiesSchema,
    products: ProjectConfigThemeProductsSchema,
    subproducts: {
      type: 'object',
      properties: {
        label: ProjectConfigThemePropertiesWithHeightSchema,
        price: ProjectConfigThemePropertiesSchema,
        nutrition: ProjectConfigThemePropertiesSchema,
        flavor: ProjectConfigThemePropertiesSchema,
        disclaimer: ProjectConfigThemePropertiesSchema,
      },
      required: ['label', 'price'],
    },
  },
  required: ['header', 'disclaimer', 'products', 'subproducts'],
};
